import { CampaignCreateParams, Provider, Subscription } from '../../../types'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { TagPicker } from '../../settings/TagPicker'
import { Column, Columns } from '../../../ui/Columns'
import { LinkButton } from '../../../ui/Button'
import { Alert } from '../../../ui'
import Heading from '../../../ui/Heading'
import CampaignProviderSelection from './CampaignProviderSelection'
import CampaignSubscriptionSelection from './CampaignSubscriptionSelection'
import SwitchField from '../../../ui/form/SwitchField'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import TextInput from '../../../ui/form/TextInput'
import RadioInput from '../../../ui/form/RadioInput'

interface AdvancedStepProps {
    form: UseFormReturn<CampaignCreateParams>
    providers: Provider[]
    subscriptions: Subscription[]
    project: any
}

export default function CampaignAdvancedStep({ form, providers, subscriptions, project }: AdvancedStepProps) {
    const { t } = useTranslation()

    const channel = useWatch({
        control: form.control,
        name: 'channel',
    })

    const type = useWatch({
        control: form.control,
        name: 'type',
    })

    const quietHoursEnabled = useWatch({
        control: form.control,
        name: 'settings.quiet_hours_enabled',
    })

    const priorities = [
        { value: 'low', label: t('priority_low') },
        { value: 'none', label: t('priority_none') },
        { value: 'high', label: t('priority_high') },
    ]

    const quietHoursActions = [
        { value: 'skip', label: t('quiet_hours_action_skip') },
        { value: 'delay', label: t('quiet_hours_action_delay') },
    ]

    const deliveryModes = [
        { key: 'broadcast', label: t('in_app_delivery_broadcast') },
        { key: 'individual', label: t('in_app_delivery_individual') },
    ]

    return (
        <>
            <Heading size="h3" title={t('wizard_advanced_title')}>
                {t('wizard_advanced_desc')}
            </Heading>

            <TagPicker.Field
                form={form}
                name="tags"
                label={t('tags')}
            />

            <Heading size="h4" title={t('wizard_advanced_channel_settings')} />
            {providers.length
                ? <Columns style={{ marginTop: '-15px' }}>
                    <Column>
                        <CampaignProviderSelection
                            providers={providers}
                            form={form}
                        />
                    </Column>
                    <Column>
                        <CampaignSubscriptionSelection
                            subscriptions={subscriptions}
                            form={form}
                        />
                    </Column>
                </Columns>
                : <Alert
                    variant="plain"
                    title={t('wizard_no_providers')}
                    actions={
                        <LinkButton to={`/projects/${project.id}/settings/integrations`}>{t('setup_integration')}</LinkButton>
                    }>{t('wizard_no_providers_desc')}</Alert>
            }

            {(channel === 'push' || channel === 'email') && <>
                <Heading size="h4" title={t('wizard_advanced_send_settings')} />
                <Columns style={{ marginTop: '-15px' }}>
                    <Column>
                        <SwitchField
                            form={form}
                            name="settings.link_wrap"
                            label={t('campaign_link_wrap')}
                            subtitle={t('campaign_link_wrap_desc')}
                        />
                    </Column>
                    <Column>
                        <SingleSelect.Field
                            form={form}
                            name="settings.priority"
                            label={t('priority')}
                            options={priorities}
                            toValue={v => v.value}
                        />
                    </Column>
                </Columns>
            </>}

            {channel === 'in_app' && type === 'blast' && <>
                <Heading size="h4" title={t('in_app_delivery')} />
                <RadioInput.Field
                    form={form}
                    name="settings.in_app_delivery"
                    subtitle={t('in_app_delivery_desc')}
                    options={deliveryModes}
                />
            </>}

            <Heading size="h4" title={t('quiet_hours')} />
            <SwitchField
                form={form}
                name="settings.quiet_hours_enabled"
                label={t('quiet_hours_enable')}
                subtitle={t('quiet_hours_desc')}
            />
            {quietHoursEnabled && <Columns style={{ marginTop: '-15px' }}>
                <Column>
                    <TextInput.Field
                        form={form}
                        name="settings.quiet_hours.start"
                        type="time"
                        label={t('quiet_hours_start')}
                        required
                    />
                </Column>
                <Column>
                    <TextInput.Field
                        form={form}
                        name="settings.quiet_hours.end"
                        type="time"
                        label={t('quiet_hours_end')}
                        required
                    />
                </Column>
                <Column>
                    <SingleSelect.Field
                        form={form}
                        name="settings.quiet_hours.action"
                        label={t('quiet_hours_action')}
                        options={quietHoursActions}
                        toValue={v => v.value}
                        required
                    />
                </Column>
            </Columns>}
        </>
    )
}
